// Trust / Credibility Section - Building trust through privacy, professionalism, and early tester program
import { Shield, Users, Award, Lock } from "lucide-react"

const Trust = () => {
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 bg-white">
      {/* Section Header */}
      <div className="text-center mb-16">
        <h2 className="text-4xl sm:text-4xl font-semibold text-gray-900 mb-4">
          Built with trust and transparency
        </h2>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
          We're building Sentivo with privacy, security, and your workflow in mind. 
          Join our early tester program and help shape the future of Mac search.
        </p>
      </div>

      {/* Trust Points Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
        <div className="text-center">
          <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-blue-600" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Privacy First</h3>
          <p className="text-gray-600">
            All data stays on your Mac. We never see, store, or transmit your files.
          </p>
        </div>

        <div className="text-center">
          <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-green-600" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Secure by Design</h3>
          <p className="text-gray-600">
            Local-first architecture with zero cloud dependencies for your sensitive data.
          </p>
        </div>

        <div className="text-center">
          <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Users className="w-8 h-8 text-purple-600" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Early Tester Program</h3>
          <p className="text-gray-600">
            Join 100 beta testers who are shaping Sentivo's development and features.
          </p>
        </div>

        <div className="text-center">
          <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Award className="w-8 h-8 text-amber-600" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Professional Quality</h3>
          <p className="text-gray-600">
            Built with care for Mac users who demand speed, reliability, and polish.
          </p>
        </div>
      </div>

      {/* Early Testers Section */}
      <div className="bg-gradient-to-br from-gray-50 to-blue-50/30 rounded-2xl p-8 md:p-12 border border-gray-200">
        <div className="max-w-3xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <Users className="w-8 h-8 text-blue-600" />
            <h3 className="text-2xl font-bold text-gray-900">Trusted by Early Testers</h3>
          </div>
          <p className="text-lg text-gray-700 mb-8 leading-relaxed">
            Sentivo is currently in private beta with a select group of early testers. 
            Their feedback is directly shaping the product, ensuring we build exactly what Mac users need.
          </p>
          
          {/* Placeholder for testimonials or avatars */}
          <div className="flex items-center justify-center gap-4 mb-6">
            {/* Placeholder avatars */}
            <div className="flex -space-x-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <div
                  key={i}
                  className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 border-2 border-white shadow-sm flex items-center justify-center text-white font-semibold text-sm"
                >
                  {String.fromCharCode(64 + i)}
                </div>
              ))}
            </div>
            <div className="text-left">
              <p className="font-semibold text-gray-900">100+ Beta Testers</p>
              <p className="text-sm text-gray-600">Actively using Sentivo</p>
            </div>
          </div>

          {/* Optional: Founder background */}
          <div className="pt-6 border-t border-gray-200">
            <p className="text-sm text-gray-600">
              Built by Mac enthusiasts who understand the frustration of scattered files and slow searches.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Trust;

